import { EntityId, PrintServiceProductEntityHydrated } from "@jackfruit/common"
import { SagaIterator } from "@redux-saga/types"
import { PayloadAction } from "@reduxjs/toolkit"
import { uniq } from "lodash"
import { call, getContext, put, select, takeLatest } from "redux-saga/effects"
import { CartEntity } from "~/interfaces/entities/Cart"
import { PageSessionEntity } from "~/interfaces/entities/PageSession"
import { StoreEntity } from "~/interfaces/entities/Store"
import { PrinticularApi } from "~/services/PrinticularApi"
import { actions, InitStorePayload } from "../process"
import { cartsSelector } from "../state/carts"
import { pageSessions, pageSessionsSelector } from "../state/pageSessions"
import { printServiceProductsSelector } from "../state/printServiceProducts"
import { stores } from "../state/stores"
import { RootState } from "../store"
import { getApi } from "./api"
import { getPrintServiceByRemoteId } from "./printServices"

export default function* watchProcessStoreInit() {
  yield takeLatest(actions.initStores.type, processStoreInit)
}

function* processStoreInit(
  action: PayloadAction<InitStorePayload>
): SagaIterator {
  const { printServiceId, productId } = action.payload

  yield put(stores.actions.setIsLoading(true))
  yield put(stores.actions.setHasError(false))
  yield put(stores.actions.setError(""))

  try {
    const getCurrentPageId = yield getContext("getCurrentPageId")
    const currentPageId = getCurrentPageId()

    const printicularApi: PrinticularApi = yield call(getApi)

    const pageSession: PageSessionEntity = yield select((state: RootState) =>
      pageSessionsSelector.selectById(state, currentPageId)
    )

    const cart: CartEntity = yield select((state: RootState) =>
      cartsSelector.selectById(state, pageSession.cartId)
    )

    let productCodes: EntityId[] = []

    const allPrintServiceProductIds = [productId]

    const printServiceProducts: PrintServiceProductEntityHydrated[] =
      yield select((state: RootState) =>
        printServiceProductsSelector.selectByIds(
          state,
          allPrintServiceProductIds
        )
      )
    productCodes = uniq(
      printServiceProducts.map(
        printServiceProduct => printServiceProduct?.productCode
      )
    )

    const retailerIds = cart.retailerIds

    // and add this list to the available product ids on the page session
    let allStores: StoreEntity[] = yield call(() =>
      printicularApi.getClosestAvailableStoresForMultiplePrintServices(
        [printServiceId],
        productCodes,
        retailerIds
      )
    )

    // apply print service local id to store entities
    for (let i = 0; i < allStores.length; i++) {
      const store = allStores[i]
      const localPrintService = yield call(getPrintServiceByRemoteId, {
        remoteId: store.printServiceId,
      })
      allStores[i].printServiceId = localPrintService.id
    }

    // upsert store list
    yield put(stores.actions.upsertMany(allStores))
    // update available store to page session
    yield put(
      pageSessions.actions.updateOne({
        id: pageSession.id,
        changes: {
          storeIds: allStores.map((s: StoreEntity) => s.id),
          hasLoadedStores: allStores.length > 0,
        },
      })
    )
    // set default selected store
    if (allStores.length > 0) {
      const previouslySelectedId = cart.storeId

      const foundStore = allStores.find(
        (store: StoreEntity) => store.id === previouslySelectedId
      )
      const storeToSelect = foundStore ?? allStores[0]

      yield put(
        actions.updateStore({
          storeId: storeToSelect.id,
        })
      )
    }
  } catch (error: any) {
    console.error(error)
    yield put(stores.actions.setError(error.message))
    yield put(stores.actions.setHasError(true))
  } finally {
    yield put(stores.actions.setIsLoading(false))
    yield put(
      actions.updateOrderSummary({
        reason: "store search",
        reasonType: "storeChanged",
      })
    )
  }
}
